/**
 * ParticipantsStep — Create Test Wizard
 * Step 6F Green Phase
 */

import React, { useRef, useState, useMemo } from "react";
import { apiClient, ServiceError } from "../../services/apiClient";
import { showToast } from "../../lib/toast";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { DataTable, type Column } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import { XlsxErrorList, type XlsxRowError } from "../../components/XlsxErrorList";
import type { WizardStepProps } from "./VersionSelectionStep";
import { Upload, CheckCircle, Download, Info } from "lucide-react";

interface Participant {
  id: string;
  email: string;
  name: string;
  linkedin_url: string;
  status: string;
}

interface ParsedParticipantResponse {
  participants: Array<{ name: string; email: string; linkedin_url?: string }>;
  errors: XlsxRowError[];
}

// Avatar helper
const AVATAR_COLORS = [
  "bg-amber-100 text-amber-700",
  "bg-green-100 text-green-700",
  "bg-blue-100 text-blue-700",
  "bg-purple-100 text-purple-700",
  "bg-red-100 text-red-700",
  "bg-teal-100 text-teal-700",
];

function getInitials(name: string) {
  return name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function ParticipantsStep({ wizardData, onNext, onBack }: WizardStepProps) {
  const isBaseline = Boolean(wizardData?.is_baseline_source);
  const hasParticipant = (arr: Participant[]) => isBaseline && arr.length >= 1;

  const [participants, setParticipants] = useState<Participant[]>(() => {
    const saved = wizardData?.participants;
    if (Array.isArray(saved) && saved.length > 0) {
      return saved.map((p: Record<string, unknown>, i: number) => ({
        id: (p.id as string) ?? `temp-${i}`,
        email: (p.email as string) ?? "",
        name: (p.name as string) ?? "",
        linkedin_url: (p.linkedin_url as string) ?? "",
        status: (p.status as string) ?? "pending",
      }));
    }
    return [];
  });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<XlsxRowError[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = async () => {
    try {
      const { blob, filename } = await apiClient.getBlob("/api/tests/participants/import-template");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename ?? "participants-import-template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not download template.";
      showToast({ variant: "error", title: "Download failed", description: message });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setUploadError("Invalid file format. Please upload an .xlsx file.");
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setRowErrors([]);

    try {
      const result = await apiClient.postFile<ParsedParticipantResponse>(
        "/api/tests/participants/parse",
        file,
      );
      const parsed: Participant[] = result.participants.map((p, i) => ({
        id: `temp-${i}`,
        email: p.email,
        name: p.name,
        linkedin_url: p.linkedin_url ?? "",
        status: "pending",
      }));
      if (isBaseline && parsed.length > 1) {
        setUploadError("Baseline tests are limited to one candidate. Upload a file with exactly one participant.");
        showToast({
          variant: "error",
          title: "Too many participants",
          description: "Baseline tests are limited to one candidate.",
        });
        return;
      }
      setParticipants(parsed);
      showToast({
        variant: "success",
        title: "Participants uploaded",
        description: `${parsed.length} participants loaded from "${file.name}".`,
      });
    } catch (err) {
      if (err instanceof ServiceError && err.details && Array.isArray(err.details.errors)) {
        const errors = err.details.errors as XlsxRowError[];
        setRowErrors(errors);
        showToast({
          variant: "error",
          title: "Import rejected",
          description: `${errors.length} row issue${errors.length === 1 ? "" : "s"}. See details below.`,
        });
      } else {
        const message = err instanceof Error ? err.message : "Upload failed";
        setUploadError(message);
      }
    } finally {
      setIsUploading(false);
      // Reset the input so the same file can be re-picked after fixing.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const columns: Column<Participant>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Candidate",
        render: (_v, row) => (
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold shrink-0 ${getAvatarColor(
                row.name
              )}`}
              aria-hidden="true"
            >
              {getInitials(row.name)}
            </span>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-900">{row.name}</span>
              <span className="text-xs text-brand-steel">{row.email}</span>
            </div>
          </div>
        ),
      },
      {
        key: "linkedin_url",
        header: "LinkedIn",
        render: (_v, row) =>
          row.linkedin_url ? (
            <a
              href={row.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-brand-crimson hover:underline truncate inline-block max-w-[18rem]"
              title={row.linkedin_url}
            >
              {row.linkedin_url}
            </a>
          ) : (
            <span className="text-xs text-brand-steel/60">—</span>
          ),
      },
      {
        key: "status",
        header: "Status",
        render: (_v, row) => (
          <StatusBadge variant="pending">{row.status === "pending" ? "Pending" : row.status}</StatusBadge>
        ),
      },
    ],
    []
  );

  const uploadDisabled = isUploading || hasParticipant(participants);

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Add Participants</h2>
      <p className="text-sm text-brand-steel mb-6">
        Download the template, fill one row per participant (Name + Email + LinkedIn URL),
        and upload as .xlsx. LinkedIn URL is required.
      </p>

      {isBaseline && (
        <div className="flex items-start gap-2 p-4 mb-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-800">
          <Info size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm">
            Baseline source tests are limited to <strong>one candidate</strong>. Upload an .xlsx file with exactly one participant.
          </p>
        </div>
      )}

      {/* Toolbar: Download Template + Upload */}
      <div className="flex items-center gap-3 mb-4">
        <Button
          type="button"
          variant="outline"
          onClick={handleDownloadTemplate}
          className="inline-flex items-center gap-2"
        >
          <Download size={16} aria-hidden="true" /> Download Template
        </Button>
        <Button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadDisabled}
          className="inline-flex items-center gap-2"
        >
          <Upload size={16} aria-hidden="true" />
          {isUploading ? "Uploading..." : "Upload XLSX"}
        </Button>
      </div>

      {/* Upload dropzone */}
      <Card
        className={`border-2 border-dashed mb-4 ring-0 transition-colors ${uploadDisabled ? "border-brand-steel/30 opacity-50 cursor-not-allowed" : "border-brand-steel/50 cursor-pointer"}`}
        onClick={() => { if (!uploadDisabled) fileInputRef.current?.click(); }}
        role="button"
        tabIndex={uploadDisabled ? -1 : 0}
        aria-label="Upload participants .xlsx file"
        aria-disabled={uploadDisabled}
        onKeyDown={(e) => {
          if (uploadDisabled) return;
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
      >
        <CardContent className="py-1 px-8 flex flex-col items-center justify-center text-center">
          <Upload size={32} className="text-brand-steel/40 mb-3" aria-hidden="true" />
          <p className="text-sm text-brand-navy/60">
            Drop an .xlsx file here or <span className="text-brand-crimson font-medium">browse</span>
          </p>
          <p className="text-xs text-brand-steel mt-1">Use the downloaded template — server-validated on upload.</p>
        </CardContent>
      </Card>
      <input ref={fileInputRef} type="file" accept=".xlsx" onChange={handleFileChange} className="hidden" />
      {uploadError && (
        <div role="alert" className="mb-2 text-xs text-brand-error">
          {uploadError}
        </div>
      )}
      {rowErrors.length > 0 && (
        <div className="mb-4">
          <XlsxErrorList errors={rowErrors} />
        </div>
      )}

      {/* Loaded participants table */}
      {participants.length > 0 && (
        <div className="mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-4 rounded-full text-xs font-medium bg-green-50 text-brand-success border border-brand-success/30">
            <CheckCircle size={14} aria-hidden="true" />
            {participants.length} participants loaded
          </div>
          <DataTable<Participant>
            data={participants}
            columns={columns}
            keyField="id"
            emptyMessage="No participants uploaded."
          />
        </div>
      )}

      <div className="flex justify-between">
        <div>
          {onBack && (
            <Button type="button" variant="outline" onClick={() => onBack?.()} className="min-w-25 border-brand-steel/40">
              Back
            </Button>
          )}
        </div>
        <Button
          type="button"
          onClick={() => onNext?.({ participants })}
          disabled={participants.length === 0}
          className="min-w-25 bg-brand-crimson hover:bg-brand-crimson/90 text-white font-medium text-sm rounded-lg px-6 py-2"
        >
          Next
        </Button>
      </div>
    </div>
  );
}

export default ParticipantsStep;
